import React, { useEffect, useState } from 'react'
import { useSnapshot } from 'valtio'
import {
  activateIpGameSession,
  changeAuthEmail,
  changeAuthPassword,
  deleteAuthAccount,
} from '../../api/utopiaAuthApi'
import {
  clearUtopiaAuthSession,
  logoutUtopiaAuth,
  patchUtopiaAuthUser,
  refreshUtopiaIpGameSession,
  setUtopiaIpGameSession,
  utopiaAuthState,
} from './utopiaAuthState'
import { getUtopiaPlayButtonState, utopiaServerStatusState } from './utopiaServerStatus'
import styles from './utopiaLanding.module.css'

type Panel = 'main' | 'settings' | 'password' | 'email' | 'delete' | 'ipSession'

type Props = {
  onClose?: () => void
  onPlayClick?: () => void
  onOptionsClick?: () => void
}

export default function UtopiaAccountMenu ({ onClose, onPlayClick, onOptionsClick }: Props) {
  const { user, ipGameSession } = useSnapshot(utopiaAuthState)
  useSnapshot(utopiaServerStatusState)
  const playAccount = getUtopiaPlayButtonState('account')
  const [panel, setPanel] = useState<Panel>('main')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [emailPassword, setEmailPassword] = useState('')
  const [email, setEmail] = useState(user?.email ?? '')
  const [deletePassword, setDeletePassword] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(false)

  useEffect(() => {
    setEmail(user?.email ?? '')
  }, [user?.email])

  if (!user) return null

  const resetMessages = () => {
    setError(null)
    setSuccess(null)
  }

  const clearFormFields = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setEmailPassword('')
    setDeletePassword('')
    setDeleteConfirm(false)
  }

  const goMain = () => {
    setPanel('main')
    resetMessages()
    clearFormFields()
  }

  const goSettings = () => {
    setPanel('settings')
    resetMessages()
    clearFormFields()
  }

  const handlePlay = () => {
    if (playAccount.disabled) return
    onClose?.()
    onPlayClick?.()
  }

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    resetMessages()
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas')
      return
    }
    setLoading(true)
    try {
      await changeAuthPassword(currentPassword, newPassword)
      setSuccess('Mot de passe modifié')
      goSettings()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    resetMessages()
    setLoading(true)
    try {
      const result = await changeAuthEmail(emailPassword, email.trim())
      patchUtopiaAuthUser({ email: result.user.email })
      setSuccess('E-mail mis à jour')
      goSettings()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  const submitDelete = async (e: React.FormEvent) => {
    e.preventDefault()
    resetMessages()
    if (!deleteConfirm) {
      setError('Veuillez confirmer la suppression du compte')
      return
    }
    setLoading(true)
    try {
      await deleteAuthAccount(deletePassword)
      clearUtopiaAuthSession()
      onClose?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    await logoutUtopiaAuth()
    onClose?.()
  }

  if (panel === 'ipSession') {
    const hadActiveSession = ipGameSession != null && ipGameSession.expiresAt > Date.now()
    return (
      <IpSessionPanel
        ipGameSession={ipGameSession}
        onBack={goMain}
        error={error}
        success={success}
        loading={loading}
        onActivate={async () => {
          resetMessages()
          setLoading(true)
          try {
            const result = await activateIpGameSession()
            if (result.expiresAt != null && result.timeoutMinutes != null) {
              setUtopiaIpGameSession({
                ipMasked: result.ipMasked ?? null,
                requestIpMasked: result.requestIpMasked ?? null,
                expiresAt: result.expiresAt,
                timeoutMinutes: result.timeoutMinutes,
              })
            }
            setSuccess(
              hadActiveSession
                ? 'Session IP mise à jour pour ce réseau.'
                : 'Session IP activée. Vous pouvez rejoindre le serveur sans retaper /login.',
            )
          } catch (err) {
            setError(err instanceof Error ? err.message : String(err))
          } finally {
            setLoading(false)
          }
        }}
      />
    )
  }

  if (panel === 'settings') {
    return (
      <AccountSubPanel title="Paramètres du compte" onBack={goMain}>
        <button
          type="button"
          className={styles.authAccountAction}
          onClick={() => { resetMessages(); setPanel('password') }}
        >
          Modifier le mot de passe
        </button>
        <button
          type="button"
          className={styles.authAccountAction}
          onClick={() => {
            resetMessages()
            setEmail(user.email ?? '')
            setPanel('email')
          }}
        >
          Modifier l&apos;e-mail
        </button>
        <button
          type="button"
          className={`${styles.authAccountAction} ${styles.authAccountActionDanger}`}
          onClick={() => {
            resetMessages()
            setDeletePassword('')
            setDeleteConfirm(false)
            setPanel('delete')
          }}
        >
          Supprimer le compte
        </button>
      </AccountSubPanel>
    )
  }

  if (panel === 'password') {
    return (
      <AccountSubPanel title="Modifier le mot de passe" onBack={goSettings}>
        <form onSubmit={e => void submitPassword(e)}>
          <PasswordField id="cur-pw" label="Mot de passe actuel" value={currentPassword} onChange={setCurrentPassword} />
          <PasswordField
            id="new-pw"
            label="Nouveau mot de passe"
            value={newPassword}
            onChange={setNewPassword}
            minLength={10}
            autoComplete="new-password"
            hint="Au moins 10 caractères, une lettre et un chiffre ; évitez les mots courants et votre pseudo."
          />
          <PasswordField
            id="conf-pw"
            label="Confirmer le mot de passe"
            value={confirmPassword}
            onChange={setConfirmPassword}
            minLength={10}
            autoComplete="new-password"
          />
          <FormFooter error={error} success={success} loading={loading} submitLabel="Enregistrer" />
        </form>
      </AccountSubPanel>
    )
  }

  if (panel === 'email') {
    return (
      <AccountSubPanel title="Modifier l'e-mail" onBack={goSettings}>
        <form onSubmit={e => void submitEmail(e)}>
          <div className={styles.authField}>
            <label className={styles.authLabel} htmlFor="acc-email">E-mail</label>
            <input
              id="acc-email"
              className={styles.authInput}
              type="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>
          <PasswordField id="email-pw" label="Mot de passe (confirmation)" value={emailPassword} onChange={setEmailPassword} />
          <FormFooter error={error} success={success} loading={loading} submitLabel="Enregistrer" />
        </form>
      </AccountSubPanel>
    )
  }

  if (panel === 'delete') {
    return (
      <AccountSubPanel title="Supprimer le compte" onBack={goSettings}>
        <p className={styles.authDeleteWarn}>
          Cette action est irréversible. Toutes vos sessions seront supprimées.
        </p>
        <form onSubmit={e => void submitDelete(e)}>
          <PasswordField id="del-pw" label="Mot de passe" value={deletePassword} onChange={setDeletePassword} />
          <label className={styles.authTermsRow}>
            <input
              type="checkbox"
              className={styles.authTermsCheckbox}
              checked={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.checked)}
            />
            <span className={styles.authTermsText}>
              Je confirme vouloir supprimer définitivement mon compte <strong>{user.gameUsername}</strong>
            </span>
          </label>
          <FormFooter error={error} success={success} loading={loading} submitLabel="Supprimer mon compte" danger />
        </form>
      </AccountSubPanel>
    )
  }

  return (
    <div className={styles.authLoggedIn}>
      <button
        type="button"
        className={styles.authPlayAccountBtn}
        onClick={handlePlay}
        disabled={playAccount.disabled}
        title={playAccount.disabled ? playAccount.label : undefined}
      >
        ▶ {playAccount.label}
      </button>

      <p className={styles.authLoggedInUser}>
        Connecté en tant que <strong>{user.gameUsername}</strong>
      </p>

      <button
        type="button"
        className={styles.authAccountSettingsBtn}
        onClick={goSettings}
      >
        Paramètres du compte
      </button>

      <button
        type="button"
        className={styles.authAccountSettingsBtn}
        onClick={() => {
          resetMessages()
          setPanel('ipSession')
        }}
      >
        Session IP
        {ipGameSession ? ' ✓' : ''}
      </button>

      {onOptionsClick && (
        <button type="button" className={styles.authOptionsBtn} onClick={onOptionsClick}>
          {translate('Game Options')}
        </button>
      )}
      <button type="button" className={styles.authLogoutBtn} onClick={() => void handleLogout()}>
        Se déconnecter
      </button>
    </div>
  )
}

function formatIpSessionRemaining (expiresAt: number): string {
  const ms = expiresAt - Date.now()
  if (ms <= 0) return 'Expirée'
  const totalMinutes = Math.floor(ms / 60_000)
  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
  const minutes = totalMinutes % 60
  if (days > 0) return `${days} j ${hours} h restantes`
  if (hours > 0) return `${hours} h ${minutes} min restantes`
  return `${minutes} min restantes`
}

function formatIpSessionExpiry (expiresAt: number): string {
  return new Date(expiresAt).toLocaleString('fr-FR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function IpSessionPanel ({
  ipGameSession,
  onBack,
  error,
  success,
  loading,
  onActivate,
}: {
  ipGameSession: { ipMasked: string | null, requestIpMasked?: string | null, expiresAt: number, timeoutMinutes: number } | null
  onBack: () => void
  error: string | null
  success: string | null
  loading: boolean
  onActivate: () => void | Promise<void>
}) {
  const [statusLoading, setStatusLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setStatusLoading(true)
      try {
        await refreshUtopiaIpGameSession()
      } finally {
        if (!cancelled) setStatusLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const isActive = ipGameSession != null && ipGameSession.expiresAt > Date.now()

  return (
    <div className={styles.authAccountSub}>
      <button type="button" className={styles.authAccountBack} onClick={onBack}>
        ← Retour
      </button>
      <h3 className={styles.authAccountSubTitle}>Session IP</h3>
      <div className={styles.authIpSessionInfo}>
        <p>
          La session IP permet de rejoindre le serveur <strong>sans retaper /login</strong>,
          si vous êtes sur le <strong>même réseau</strong> que lors de l&apos;activation
          (même box Internet, même Wi‑Fi).
        </p>
        <ul className={styles.authIpSessionList}>
          <li>Activez la session depuis l&apos;appareil que vous utiliserez pour jouer.</li>
          <li>Le PC, le téléphone ou la tablette doit partager la même IP publique que le launcher.</li>
          <li>Après activation, lancez le jeu : la connexion Utopie se fera automatiquement.</li>
          <li>Si vous changez de réseau (4G, autre box), refaites /login ou réactivez ici.</li>
        </ul>
      </div>

      {statusLoading && !ipGameSession ? (
        <p className={styles.authIpSessionLoading}>Chargement de la session…</p>
      ) : null}

      {isActive && ipGameSession ? (
        <div className={styles.authIpSessionStatus}>
          <p className={styles.authIpSessionStatusTitle}>Session active</p>
          <p className={styles.authIpSessionStatusRow}>
            <span className={styles.authIpSessionStatusLabel}>IP enregistrée</span>
            <span className={styles.authIpSessionStatusValue}>
              {ipGameSession.ipMasked ?? '— (cliquez « Mettre à jour »)'}
            </span>
          </p>
          <p className={styles.authIpSessionStatusRow}>
            <span className={styles.authIpSessionStatusLabel}>IP actuelle (site)</span>
            <span className={styles.authIpSessionStatusValue}>
              {ipGameSession.requestIpMasked ?? '—'}
            </span>
          </p>
          <p className={styles.authIpSessionStatusRow}>
            <span className={styles.authIpSessionStatusLabel}>Temps restant</span>
            <span className={styles.authIpSessionStatusValue}>
              {formatIpSessionRemaining(ipGameSession.expiresAt)}
            </span>
          </p>
          <p className={styles.authIpSessionStatusRow}>
            <span className={styles.authIpSessionStatusLabel}>Expire le</span>
            <span className={styles.authIpSessionStatusValue}>
              {formatIpSessionExpiry(ipGameSession.expiresAt)}
            </span>
          </p>
        </div>
      ) : ipGameSession ? (
        <p className={styles.authIpSessionExpired}>Session expirée — réactivez-la pour ce réseau.</p>
      ) : null}

      <div className={styles.authError} role="alert">{error ?? success ?? ''}</div>
      <button
        type="button"
        className={styles.authSubmit}
        disabled={loading}
        onClick={() => void onActivate()}
      >
        {loading
          ? (isActive ? 'Mise à jour…' : 'Activation…')
          : (isActive ? 'Mettre à jour la session' : 'Activer maintenant')}
      </button>
    </div>
  )
}

function AccountSubPanel ({
  title,
  onBack,
  children,
}: {
  title: string
  onBack: () => void
  children: React.ReactNode
}) {
  return (
    <div className={styles.authAccountSub}>
      <button type="button" className={styles.authAccountBack} onClick={onBack}>
        ← Retour
      </button>
      <h3 className={styles.authAccountSubTitle}>{title}</h3>
      {children}
    </div>
  )
}

function PasswordField ({
  id,
  label,
  value,
  onChange,
  minLength,
  autoComplete = 'current-password',
  hint,
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  minLength?: number
  autoComplete?: string
  hint?: string
}) {
  return (
    <div className={styles.authField}>
      <label className={styles.authLabel} htmlFor={id}>{label}</label>
      <input
        id={id}
        className={styles.authInput}
        type="password"
        autoComplete={autoComplete}
        value={value}
        onChange={e => onChange(e.target.value)}
        required
        minLength={minLength}
      />
      {hint ? <p className={styles.authHint}>{hint}</p> : null}
    </div>
  )
}

function FormFooter ({
  error,
  success,
  loading,
  submitLabel,
  danger,
}: {
  error: string | null
  success: string | null
  loading: boolean
  submitLabel: string
  danger?: boolean
}) {
  return (
    <>
      <div className={styles.authError} role="alert">{error ?? success ?? ''}</div>
      <button
        type="submit"
        className={danger ? styles.authDeleteSubmit : styles.authSubmit}
        disabled={loading}
      >
        {loading ? 'Chargement…' : submitLabel}
      </button>
    </>
  )
}
